/**
 * #3132 — domain-default language with a persisted user override.
 *
 * `hofsync.de` and `hofsync.com` are CO-SERVED from a single build
 * (`infra/terraform/modules/alb/main.tf` — the `.com`→`.de` 301 was removed in
 * #2006), so the domain is only knowable at RUNTIME. A Vite `import.meta.env`
 * flag cannot express it: there is one artefact for both hosts.
 *
 * Everything here is a pure function taking the hostname as a parameter.
 * `src/i18n.ts` resolves the language at MODULE IMPORT, before React exists, so
 * a `vi.stubGlobal` in a test body is already too late — keeping the resolver
 * pure is what makes it testable at all.
 */

export type AppLanguage = 'de' | 'en' | 'ro' | 'pl';

/**
 * The languages the app ships translations for. `ro` and `pl` (HOF-696 D19) are
 * SCOPED to what a worker sees — login, the worker shell, the labour flow,
 * log-out and the shared errors; every other key falls back to German.
 */
export const SUPPORTED_LANGUAGES: readonly AppLanguage[] = ['de', 'en', 'ro', 'pl'];

/**
 * HOF-696 D19 — the locale namespaces RO and PL translate: what a worker sees
 * (login + OTP, the shared errors and toasts, the invite page, and `labour`,
 * which holds the worker shell and the labour flow). Pinned by
 * `locales/__tests__/workerScopedLocales.test.ts`.
 */
export const WORKER_SCOPED_NAMESPACES: readonly string[] = [
  'common',
  'login',
  'otp',
  'errors',
  'toast',
  'inviteRequired',
  'labour',
  // The login page's cookie notice and footer (HOF-696 ui e2e: they stayed German for RO/PL).
  'cookieNotice',
  'footer',
];

/** Picker entries, each named in its own language (Footer + worker header). */
export const LANGUAGE_OPTIONS: readonly { value: AppLanguage; label: string }[] = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
  { value: 'ro', label: 'Română' },
  { value: 'pl', label: 'Polski' },
];

/**
 * The languages the `navigator` rung may pick (HOF-696 D19). Only the worker
 * languages: German and English stay with the #3132 domain default, which is a
 * deliberate per-domain choice — an en-US browser on hofsync.de keeps German, as
 * it did before (and as `e2e/tests/i18n.spec.ts`, which runs an en-US browser
 * against a German host, pins).
 */
const NAVIGATOR_LANGUAGES: readonly AppLanguage[] = ['ro', 'pl'];

/** Persisted language override (#3132). Read on boot, written on every change. */
export const LANGUAGE_COOKIE = 'hofsync_lang';

/**
 * Pre-#3132 storage for the same choice, still written by i18next's
 * LanguageDetector cache. Kept as a fallback rung so existing users are not
 * silently reset on their first load after this deploys.
 */
export const LEGACY_LANGUAGE_STORAGE_KEY = 'i18nextLng';

/** 12 months, matching the cookie notice's lifetime. */
export const LANGUAGE_COOKIE_DAYS = 365;

export function isSupportedLanguage(value: string | null | undefined): value is AppLanguage {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** The first browser language the navigator rung may pick, by primary subtag. */
export function navigatorLanguage(languages: readonly string[] | null | undefined): AppLanguage | null {
  for (const tag of languages ?? []) {
    const base = (tag ?? '').toLowerCase().split('-')[0];
    const match = NAVIGATOR_LANGUAGES.find((lng) => lng === base);
    if (match) return match;
  }
  return null;
}

/**
 * The default language for a host, used only when the visitor has expressed no
 * preference.
 *
 * German is the CATCH-ALL, and deliberately so: `localhost`, `127.0.0.1`,
 * `nginx` (the Docker e2e host) and preview hosts must all resolve German or
 * the existing suites red — `src/__tests__/i18n.test.ts` asserts German by
 * default and `e2e/tests/i18n.spec.ts` asserts German copy on first load.
 *
 * The check is against the registrable domain rather than a bare `endsWith`,
 * so an unrelated host like `nothofsync.com` does not inherit English.
 */
export function domainDefaultLanguage(hostname: string): AppLanguage {
  // Hostnames are case-insensitive, and a fully-qualified name may carry a
  // trailing dot (`hofsync.com.`) that never appears in `window.location` but
  // costs nothing to tolerate.
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');

  if (host === 'hofsync.com' || host.endsWith('.hofsync.com')) return 'en';

  return 'de';
}

export interface InitialLanguageInput {
  /** Value of the `hofsync_lang` cookie, if any. */
  cookie?: string | null;
  /** Value of the legacy `i18nextLng` localStorage entry, if any. */
  legacyLocalStorage?: string | null;
  /** `window.location.hostname`, passed in rather than read here. */
  hostname?: string | null;
  /** `navigator.languages` (HOF-696), passed in rather than read here. */
  navigatorLanguages?: readonly string[] | null;
}

/**
 * The resolution ladder, in priority order:
 *
 *   `hofsync_lang` cookie → legacy `i18nextLng` → browser (ro/pl only, HOF-696)
 *   → domain default → `de`
 *
 * Every rung is validated, so a stale or hand-edited value (`fr`, `''`) falls
 * through to the next rung instead of sticking and leaving the user on a
 * language the app cannot render.
 */
export function resolveInitialLanguage(input: InitialLanguageInput): AppLanguage {
  if (isSupportedLanguage(input.cookie)) return input.cookie;
  if (isSupportedLanguage(input.legacyLocalStorage)) return input.legacyLocalStorage;
  const fromBrowser = navigatorLanguage(input.navigatorLanguages);
  if (fromBrowser) return fromBrowser;

  return domainDefaultLanguage(input.hostname ?? '');
}
